/* eslint-disable @typescript-eslint/no-array-constructor */
import { useState, useEffect, useRef } from "react";
import DatePicker from "react-datepicker";
import { useFilePicker } from 'use-file-picker';
import {
  addPetVaccination,
  getMedicalRecordRequets,
  updatePet,
  uploadDocument,
  updateMedicalRecordRequestStatus,
  updateDocumentVetName,
  editPetVaccination,
  deletePetVaccination,
  deleteAllPetVaccination,
  getVaccineTypes,
} from "../../utils/kinshipApi";
import {
  KinshipPetProfile,
  MedicalRecord,
  MedicalRecordRequest,
  Vaccination,
  VaxType,
} from "../../utils/kinshipTypes";
import {
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  TextField,
  Button,
  IconButton,
  Alert,
  Box,
  Typography,
  Select,
  MenuItem,
  Stack,
  Grid,
  TableContainer,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import "react-datepicker/dist/react-datepicker.css";
import { datePickerFormatDate, getDateYearPlusOne, getLuxonDate, resolveDueString} from "../../utils/utils";
import ConfirmActionDialog from "../dailogs/ConfirmActionDialog";
import { StyledButton } from '../../styles/buttons';

interface Props {
  pet?: KinshipPetProfile;
  onActionVaccination?: Function;
}

export function MedicalRecordView({ 
  pet, 
  onActionVaccination = () => {}, 
}: Props) {
  const [record, setRecord] = useState<MedicalRecord>();
  const [vaxTypeList, setVaxTypeList] = useState<Array<VaxType>>([]);
  const [actionTypeState, setActionTypeState] = useState<string>("");
  const [actionArgState, setActionArgState] = useState<string>("");
  const [newVaxName, setNewVaxName] = useState("");
  const [vaxType, setVaxType] = useState<string>("");
  const [newVaxDate, setNewVaxDate] = useState<Date | null>(new Date());
  const [newVaxRenewalDate, setNewVaxRenewalDate] = useState<Date| null>(getDateYearPlusOne());
  const [oldVaccineTypeToUpdate, setOldVaccineTypeToUpdate] = useState<string>();
  const [isUpdateVaccination, setIsUpdateVaccination] = useState<boolean>(false);
  const [weight, setWeight] = useState(pet?.weight || "");
  const [microchipId, setMicrochipId] = useState("");
  const [petUpdateResult, setPetUpdateResult] = useState("");
  const [fileUpload, setFileUpload] = useState<File>();
  const [recordRequests, setRecordRequests] = useState<
    Array<MedicalRecordRequest>
  >([]);
  const [isConfirmActionDialogOpened, setIsConfirmActionDialogOpened] = useState(false);
  const renewalDatePickerRef = useRef<any>();
  const [vetName, setVetName] = useState("");
  const { openFilePicker, loading, errors, plainFiles} = useFilePicker({
    readAs: 'BinaryString',
    accept: '*/*',
    multiple: true,
    onFilesSelected: async ({ plainFiles: plainFilesProp }) => {
        setFileUpload(plainFilesProp[0]);
    },
    onFilesSuccessfullySelected: async ({ plainFiles: plainFilesProp }) => {
        setFileUpload(plainFilesProp[0]);
    },
  });

  useEffect(() => {
    setRecord(pet?.medicalRecord);
    setWeight(pet?.weight || "");
    setMicrochipId(pet?.microchipId || "");
    getMedicalRecordRequets(pet?.kinshipPetId).then((res: any) => {
      setRecordRequests(res.data);
    });
  }, [pet]);

  useEffect(() => {
    const mountAndInit = async () => {
      const vType: any = await getVaccineTypes();
      const vTypeList: Array<VaxType> = vType?.data?.vaccines as Array<VaxType>;
      const vTypeFiltered = new Array();
      // eslint-disable-next-line array-callback-return
      vTypeList.map((item: VaxType) => {
        const shouldKeep = item?.species?.some(speciesStr => pet?.petType === speciesStr);
        if(shouldKeep) {
          vTypeFiltered.push(item);
        }
      });
      setVaxTypeList(vTypeFiltered ?? []);
    };
    mountAndInit();
  }, [pet?.petType]);

  const onSubmit = (event: any) => {
    event.preventDefault();
    updatePet(pet?.kinshipPetId, weight, microchipId);
    setPetUpdateResult("Success");
  };

  const onEditVaccinationSubmit = (event: any, vaccination: Vaccination) => {
    event.preventDefault();
    
    setIsUpdateVaccination(true);
    setOldVaccineTypeToUpdate(vaccination.type ?? "");
    setNewVaxName(vaccination.vaccineName ?? "");
    setNewVaxDate(new Date(datePickerFormatDate(getLuxonDate(vaccination.administeredDate))));
    setNewVaxRenewalDate((vaccination.renewalDate || getLuxonDate(vaccination.renewalDate).isValid) ? new Date(datePickerFormatDate(getLuxonDate(vaccination.renewalDate))) : null);
    setVaxType(vaccination?.type);
  };

  const onContinueAction = (event: any) => {
    setIsConfirmActionDialogOpened(false);
    onActionVaccinationSubmit(event, actionTypeState.toLocaleUpperCase(), actionArgState);
  };

  const onActionVaccinationSubmit = async (event: any, ...args: Array<string | null>) => {
    event.preventDefault();
    const actionType = args[0];
    setActionTypeState(`${actionType?.charAt(0).toUpperCase()}${actionType?.slice(1).toLowerCase()}`);
    const actionArg = args[1];
    
    setActionArgState(actionArg ?? "");
    const actionHasConfirmDialogStep = args[2];

    if(actionHasConfirmDialogStep !== "NEEDS_CONFIRMATION") {
      if(actionType === "ADD") {
        const result = await addPetVaccination(
          pet?.kinshipPetId,
          newVaxName,
          newVaxDate?.toISOString().split(".")[0] + "Z",
          (newVaxRenewalDate) ? newVaxRenewalDate?.toISOString().split(".")[0] + "Z" : null,
          vaxType
        );
        clearVaccineInputs();
        onActionVaccination(result);
      } else if(actionType === "DELETE") {
        const result = await deletePetVaccination(
          pet?.kinshipPetId,
          actionArg,
        );
        clearVaccineInputs();
        onActionVaccination(result);
      }  else if(actionType === "DELETE_ALL") {
        const result = await deleteAllPetVaccination(pet?.kinshipPetId);
        clearVaccineInputs();
        onActionVaccination(result);
      } else if(actionType === "EDIT") {
        const result = await editPetVaccination(
          pet?.kinshipPetId,
          oldVaccineTypeToUpdate,
          newVaxName,
          newVaxDate?.toISOString().split(".")[0] + "Z",
          (newVaxRenewalDate) ? newVaxRenewalDate?.toISOString().split(".")[0] + "Z" : null,
          vaxType
        );
        clearVaccineInputs();
        onActionVaccination(result);
      }
    } else {
      setIsConfirmActionDialogOpened(true);
    }
  };

  const clearVaccineInputs = (): void => {
    setNewVaxDate(new Date());
    setNewVaxRenewalDate(getDateYearPlusOne());
    setNewVaxName("");
    setVaxType("");
    setIsUpdateVaccination(false);
    setOldVaccineTypeToUpdate("");
    setActionTypeState("");
    setActionArgState("");
    setIsConfirmActionDialogOpened(false);
  };

  const onFileUpload = async () => {
    if (!fileUpload) {
      console.error('no file:', fileUpload);
      return;
    }

    const formData = new FormData();
    formData.append('file', fileUpload, fileUpload.name);

    try {
      const resp = await uploadDocument(pet?.kinshipPetId, formData);
      await updateDocumentVetName(resp?.data?.documentURL, vetName);
      onActionVaccination();
    } catch (error) {
      console.error('Error uploading file:', error, errors);
    }
  };

  const onMRRStatusChange = async (reqId: string, status: string) => {
    await updateMedicalRecordRequestStatus(reqId, status);
    getMedicalRecordRequets(pet?.kinshipPetId).then((res: any) => {
      setRecordRequests(res.data);
    });
  };

  const onVaccineTypeChange = async (type: string | null) => {
    if(type) {
      setVaxType(type);
    }
  };

  const onClearDatePickerSelection = (event: any) => {
    event.preventDefault();
    setNewVaxRenewalDate(null);
    renewalDatePickerRef.current?.setOpen(false);
  };

  const onRenewalDateChange = (date: Date): void => {
    if(date) {
      setNewVaxRenewalDate(date);
    } else {
      setNewVaxRenewalDate(null);
    }
  };

  const getVaxTypeValue = (type: string): string | undefined => {
    return vaxTypeList.find((vaxTypeObj: VaxType) => vaxTypeObj.internal_title === type)?.external_title;
  }

  useEffect(() => {
    const processData = async () => {
        if (!loading && plainFiles?.length > 0 && !plainFiles) {
        setFileUpload(plainFiles[0]);
      }
    };
    processData();
  }, [loading, plainFiles]);

  return (
    <>
      <form onSubmit={onSubmit}>
        <ConfirmActionDialog 
          ActionType={actionTypeState}
          ActionName={actionArgState === "" ? newVaxName : actionArgState}
          onContinueAction={(event) => onContinueAction(event)}
          isConfirmActionDialogOpened={isConfirmActionDialogOpened}
          setIsConfirmActionDialogOpened={setIsConfirmActionDialogOpened}
        />

        <Box
          sx={{ mt: 2, p: 4, border: 1, borderColor: "gray", borderRadius: 5, minWidth: 1200 }}
        >
          <Typography
            variant="h4"
            component="h4"
            sx={{ mb: 2, fontWeight: "medium" }}
          >
            Medical Record
          </Typography>
          <div>
            <TextField
              size="small"
              id="weight"
              name="weight"
              label="Weight"
              variant="outlined"
              value={weight}
              onChange={(e) => {
                setPetUpdateResult("");
                setWeight(e.target.value);
              }}
              margin="normal"
            />
          </div>
          <div>
            <TextField
              size="small"
              id="microchipId"
              name="microchipId"
              label="Microchip ID"
              variant="outlined"
              value={microchipId}
              margin="normal"
              onChange={(e) => {
                setPetUpdateResult("");
                setMicrochipId(e.target.value);
              }}
            />
          </div>
          <div>
            <Button type="submit" variant="contained">
              Save
            </Button>
            {!!petUpdateResult && (
              <Alert sx={{ width: 300, mt: 2 }} severity="success">
                Success
              </Alert>
            )}
          </div>
        </Box>

        <Box
          sx={{ mt: 2, p: 4, border: 1, borderColor: "gray", borderRadius: 5 }}
        >
          <Typography
            variant="h4"
            component="h4"
            sx={{ mb: 2, fontWeight: "medium" }}
          >
            Vaccinations
          </Typography>
          <TableContainer sx={{ minWidth: 1200 }}>
            <Table
                  sx={{
                    width: "max-content"
                  }}
            >
              <TableHead>
                <TableRow>
                  <TableCell align="left" colSpan={2}>
                    <Typography
                      variant="h6"
                      component="h6"
                      sx={{ fontWeight: "medium" }}
                    >
                      Name
                    </Typography>
                  </TableCell>
                  <TableCell align="left" colSpan={2}>
                    <Typography
                      variant="h6"
                      component="h6"
                      sx={{ fontWeight: "medium" }}
                    >
                      Type
                    </Typography>
                  </TableCell>
                  <TableCell align="left" colSpan={2}>
                    <Typography
                      variant="h6"
                      component="h6"
                      sx={{ fontWeight: "medium" }}
                    >
                      Administered Date
                    </Typography>
                  </TableCell>
                  <TableCell align="left" colSpan={2}>
                    <Typography
                      variant="h6"
                      component="h6"
                      sx={{ fontWeight: "medium" }}
                    >
                      Renewal Date
                    </Typography>
                  </TableCell>
                  <TableCell align="left" colSpan={2}>
                    <Typography
                      variant="h6"
                      component="h6"
                      sx={{ fontWeight: "medium" }}
                    >
                      Actions
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {record?.vaccine
                  ? record?.vaccine?.map(
                      (vaccination: Vaccination, index: number) => {
                        return (
                          <TableRow key={index}>
                            <TableCell align="left" colSpan={2}>
                              {vaccination?.vaccineName}
                              <Typography 
                                variant="body1"
                                sx={{ color: resolveDueString(vaccination.renewalDate).color }} 
                              >
                                {resolveDueString(vaccination.renewalDate).text}
                              </Typography>
                            </TableCell>
                            <TableCell align="left" colSpan={2}>{getVaxTypeValue(vaccination?.type)} </TableCell>
                            <TableCell align="left" colSpan={2}>
                              {(vaccination.administeredDate || getLuxonDate(vaccination.administeredDate).isValid) ? getLuxonDate(vaccination.administeredDate).setLocale('zh').toLocaleString() : "NOT SET"}
                            </TableCell>
                            <TableCell align="left" colSpan={2}>
                              {(vaccination.renewalDate || getLuxonDate(vaccination.renewalDate).isValid) ? getLuxonDate(vaccination.renewalDate).setLocale('zh').toLocaleString() : "NOT SET"}
                            </TableCell>
                            <TableCell align="left" colSpan={2}>
                              <Stack direction="row" spacing={1}>
                                <IconButton 
                                  color="primary" 
                                  aria-label="edit" 
                                  onClick={(event) => onEditVaccinationSubmit(event, vaccination)} 
                                >
                                  <EditIcon />
                                </IconButton>
                                <IconButton 
                                  color="primary" 
                                  aria-label="delete"
                                  onClick={(event) => onActionVaccinationSubmit(event, "DELETE", vaccination?.type, "NEEDS_CONFIRMATION")} 
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        );
                      }
                    )
                  : "No vaccinations"}
                <TableRow>
                  <TableCell align="left" colSpan={2}>
                    <TextField
                      id="newName"
                      name="newName"
                      label="New Name"
                      variant="outlined"
                      size="small"
                      value={newVaxName}
                      onChange={(e) => setNewVaxName(e.target.value)}
                    />
                  </TableCell>
                  <TableCell align="left" colSpan={2}>
                    <Select
                      value={vaxType}
                      onChange={(e) =>
                        onVaccineTypeChange(e.target.value)
                      }
                      sx={{ height: 40 }}
                    >
                      {vaxTypeList.map((val) => (
                        <MenuItem key={val.id} value={val?.internal_title}>
                          {val?.external_title}
                        </MenuItem>
                      ))}
                    </Select>
                  </TableCell>
                  <TableCell align="left" colSpan={2}>
                    <DatePicker
                      selected={newVaxDate || new Date()}
                      onChange={(date: Date) => setNewVaxDate(date)}
                      dateFormat="yyyy/MM/dd"
                      customInput={
                        <TextField
                          id="newVaxDate"
                          name="newVaxDate"
                          label="Administered Date"
                          variant="outlined"
                          size="small"
                        />
                    }
                    />
                  </TableCell>
                  <TableCell align="left" colSpan={2}>
                    <DatePicker
                      ref={renewalDatePickerRef}
                      selected={newVaxRenewalDate}
                      onChange={(date: Date) => onRenewalDateChange(date)}
                      dateFormat="yyyy/MM/dd"
                      isClearable
                      customInput={
                        <TextField
                          id="newVaxRenewalDate"
                          name="newVaxRenewalDate"
                          label="Renewal Date"
                          variant="outlined"
                          size="small"
                        />
                      }
                    >
                      <Grid
                        container
                        spacing={0}
                        direction="column"
                        alignItems="center"
                        justifyContent="center"
                        sx={{ minHeight: '5vh' }}
                      >
                        <Grid item xs={3}>
                          <Stack direction="row" spacing={1}>
                            <Button
                              type="submit"
                              variant="contained"
                              onClick={(event) => onClearDatePickerSelection(event)}
                            >
                              Clear
                            </Button>
                            <Button
                              type="submit"
                              variant="contained"
                              onClick={(event) => {
                                event.preventDefault();
                                renewalDatePickerRef.current?.setOpen(false);
                              }}
                            >
                              Apply
                            </Button>
                          </Stack>
                        </Grid>
                      </Grid>
                    </DatePicker>
                  </TableCell>
                  <TableCell align="left" colSpan={2}>
                    <Stack direction="row" spacing={1}>
                      <Button
                        type="submit"
                        variant="contained"
                        onClick={(event) => onActionVaccinationSubmit(event, `${(isUpdateVaccination) ? "EDIT" : "ADD"}`)}
                      >
                        {(isUpdateVaccination) ? "Update Vaccination" : "Add Vaccination"}
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
        <Box
          sx={{ mt: 2, p: 4, border: 1, borderColor: "gray", borderRadius: 5 }}
        >
          <Typography
            variant="h4"
            component="h4"
            sx={{ mb: 2, fontWeight: "medium" }}
          >
            Documents
          </Typography>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>File Name</TableCell>
                <TableCell>Vet Name</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pet?.documents?.vet_records?.map((doc: any, index: number) => {
                return (
                  <TableRow key={index}>
                    <TableCell>{doc?.documentURL?.split("/")?.pop()}</TableCell>
                    <TableCell>{doc?.metadata?.["vet-name"]}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <Typography
            variant="h5"
            component="h5"
            sx={{ my: 2, fontWeight: "medium" }}
          >
            Upload
          </Typography>
          <StyledButton 
            variant="contained" 
            onClick={() => openFilePicker()} 
            sx={{ mt: 2, display: "block" }}
          >
            Select File
          </StyledButton>
          <TextField
            size="small"
            label="Vet Name"
            variant="outlined"
            value={vetName}
            onChange={(e) => setVetName(e.target.value)}
            margin="normal"
            sx={{ display: "block" }}
          />
          <p>Filename: {fileUpload?.name}</p>
          <Button
            variant="contained"
            onClick={onFileUpload}
            sx={{ mt: 2, display: "block" }}
          >
            Upload!
          </Button>
        </Box>
        <Box
          sx={{ mt: 2, p: 4, border: 1, borderColor: "gray", borderRadius: 5 }}
        >
          <Typography
            variant="h4"
            component="h4"
            sx={{ mb: 2, fontWeight: "medium" }}
          >
            Medical Record Requests
          </Typography>
          {!recordRequests.length ? (
            <p>None</p>
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell align="left">Business Name</TableCell>
                  <TableCell>Request Date</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recordRequests?.map(
                  (req: MedicalRecordRequest, index: number) => {
                    return (
                      <TableRow key={index}>
                        <TableCell>{req?.business_name}</TableCell>
                        <TableCell>
                          {new Date(
                            req?.created_timestamp
                          )?.toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <Select
                            value={req?.status}
                            onChange={(e) =>
                              onMRRStatusChange(req?.id, e.target.value)
                            }
                          >
                            {[
                              "requested",
                              "pending",
                              "pet-parent-cancelled",
                              "complete",
                              "rejected",
                              "no-vax-on-file",
                              "additional-info-needed",
                            ].map((val) => (
                              <MenuItem key={val} value={val}>
                                {val}
                              </MenuItem>
                            ))}
                          </Select>
                        </TableCell>
                      </TableRow>
                    );
                  }
                )}
              </TableBody>
            </Table>
          )}
        </Box>
      </form>
    </>
  );
}

